import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../utils/api';
import './ConfigPage.css';

function CouncilConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      setError(null);
      const data = await adminAPI.getCouncilConfig();
      setConfig(data);
    } catch (err) {
      console.error('Failed to fetch council config:', err);
      setError(err.response?.data?.detail || 'Failed to load council configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleModelChange = (index, value) => {
    setConfig(prev => {
      const newModels = [...(prev.council_models || [])];
      newModels[index] = value;
      return { ...prev, council_models: newModels };
    });
    setHasChanges(true);
  };

  const handleChairmanChange = (value) => {
    setConfig(prev => ({ ...prev, chairman_model: value }));
    setHasChanges(true);
  };

  const handleAddModel = () => {
    if ((config?.council_models?.length || 0) >= 10) {
      setError('Maximum 10 council models allowed');
      return;
    }
    setConfig(prev => ({
      ...prev,
      council_models: [...(prev.council_models || []), '']
    }));
    setHasChanges(true);
  };

  const handleRemoveModel = (index) => {
    if ((config?.council_models?.length || 0) <= 2) {
      setError('Minimum 2 council models required');
      return;
    }
    setConfig(prev => ({
      ...prev,
      council_models: prev.council_models.filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Validate models
    const models = config?.council_models?.filter(m => m.trim()) || [];
    if (models.length < 2) {
      setError('At least 2 council models are required');
      return;
    }
    if (!config?.chairman_model?.trim()) {
      setError('Chairman model is required');
      return;
    }

    setSaving(true);
    try {
      const updated = await adminAPI.updateCouncilConfig({
        council_models: models,
        chairman_model: config.chairman_model
      });
      setConfig(updated);
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to save council config:', err);
      setError(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="config-page">
        <div className="page-header">
          <h1 className="page-title">Council Configuration</h1>
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <span>Loading council configuration...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="config-page">
      <div className="page-header">
        <h1 className="page-title">Council Configuration</h1>
        <p className="page-subtitle">
          Configure which models participate in the AI council and which model performs the final synthesis.
        </p>
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {hasChanges && (
        <div className="save-bar">
          <span>You have unsaved changes</span>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      <div className="config-section">
        <div className="section-header">
          <h2>Council Models</h2>
          <button className="btn btn-secondary btn-sm" onClick={handleAddModel}>
            + Add Model
          </button>
        </div>
        <p className="section-desc">
          These models participate in the council and provide their perspectives on each query.
          Order determines priority when model limits apply.
        </p>
        <div className="model-list">
          {(config?.council_models || []).map((model, index) => (
            <div key={index} className="model-item">
              <span className="model-number">#{index + 1}</span>
              <input
                type="text"
                value={model}
                onChange={(e) => handleModelChange(index, e.target.value)}
                placeholder="provider/model-name"
                className="model-input"
              />
              <button
                className="btn btn-danger btn-sm"
                onClick={() => handleRemoveModel(index)}
                disabled={(config?.council_models?.length || 0) <= 2}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="config-section">
        <h2>Chairman Model</h2>
        <p className="section-desc">
          The chairman model performs the final synthesis of all council responses into a unified answer.
        </p>
        <div className="chairman-input">
          <input
            type="text"
            value={config?.chairman_model || ''}
            onChange={(e) => handleChairmanChange(e.target.value)}
            placeholder="provider/model-name"
            className="model-input large"
          />
        </div>
      </div>

      <div className="info-box">
        <h4>Model Format</h4>
        <p>
          Use the format <code>provider/model-name</code>. Examples:
        </p>
        <ul>
          <li><code>groq/llama-3.3-70b-versatile</code></li>
          <li><code>openai/gpt-4o-mini</code></li>
          <li><code>claude/claude-3-5-haiku-20241022</code></li>
          <li><code>perplexity/llama-3.1-sonar-small-128k-online</code></li>
        </ul>
      </div>
    </div>
  );
}

export default CouncilConfig;
